import React, { useContext, useMemo } from "react";
import { IonButton, IonAvatar, IonIcon, IonSpinner, useIonAlert } from "@ionic/react";
import { personOutline, businessOutline } from "ionicons/icons";
import { ProfileContext } from "../../context/ProfileContext";
import { ASSOCIATESTATUS_TYPES } from "../../functions/postActionTypes";
import "../../theme/AppStyles.css";
import { useAssociateRequestHandlers } from "../../hooks/useAssociateRequestHandlers";

interface AssociateDetailPanelProps {
  associateId: string | null;
}

const FALLBACK_AVATAR = "../assets/profilePic.png";

export const AssociateDetailPanel: React.FC<AssociateDetailPanelProps> = ({ associateId }) => {
  const { profile, myAssociates, myRequests, everyoneElse } = useContext(ProfileContext);
  const [presentAlert] = useIonAlert();

  const {
    handleSendRequest: handleSendRequestBase,
    handleCancelRequest: handleCancelRequestBase,
    handleAcceptRequest: handleAcceptRequestBase,
    handleDeclineRequest: handleDeclineRequestBase,
    handleFireAssociate: handleFireAssociateBase,
  } = useAssociateRequestHandlers({
    profile,
    presentAlert,
    customMessages: {
      sendRequest: (target) => ({
        header: "Send Request?",
        message: `Send an associate request to ${target.firstName} ${target.lastName}?`,
      }),
      cancelRequest: {
        header: "Cancel Request?",
        message: "Are you sure you want to cancel this request?",
      },
      acceptRequest: (target) => ({
        header: "Accept Request?",
        message: `Accept associate request from ${target.firstName} ${target.lastName}?`,
      }),
      declineRequest: {
        header: "Decline Request?",
        message: "Are you sure you want to decline this request?",
      },
      fireAssociate: {
        header: "Drop Associate?",
      },
    },
  });

  // Find the associate from all available lists
  const associate = useMemo(() => {
    if (!associateId) return null;

    // Check myAssociates first
    let found = myAssociates?.find((a: any) => a.WerkerUid === associateId);
    if (found) return { ...found, status: ASSOCIATESTATUS_TYPES.Associate };

    // Check myRequests
    found = myRequests?.find((r: any) => r.WerkerUid === associateId);
    if (found) return found;

    // Check everyoneElse
    found = everyoneElse?.find((e: any) => e.WerkerUid === associateId);
    if (found) return { ...found, status: "none" };

    return null;
  }, [associateId, myAssociates, myRequests, everyoneElse]);

  // Wrapper handlers that check for associate existence
  const handleSendRequest = () => {
    if (!associate) return;
    handleSendRequestBase(
      associate.WerkerUid,
      associate.WerkerFirstName,
      associate.WerkerLastName,
      associate.WerkerCompany,
      associate.WerkerProfilePicURL
    );
  };

  const handleCancelRequest = () => {
    if (!associate) return;
    handleCancelRequestBase(
      associate.WerkerUid,
      associate.WerkerFirstName,
      associate.WerkerLastName,
      associate.WerkerCompany,
      associate.WerkerProfilePicURL
    );
  };

  const handleAcceptRequest = () => {
    if (!associate) return;
    handleAcceptRequestBase(
      associate.WerkerUid,
      associate.WerkerFirstName,
      associate.WerkerLastName,
      associate.WerkerCompany,
      associate.WerkerProfilePicURL
    );
  };

  const handleDeclineRequest = () => {
    if (!associate) return;
    handleDeclineRequestBase(
      associate.WerkerUid,
      associate.WerkerFirstName,
      associate.WerkerLastName,
      associate.WerkerCompany,
      associate.WerkerProfilePicURL
    );
  };

  const handleDropAssociate = () => {
    if (!associate) return;
    handleFireAssociateBase(
      associate.WerkerUid,
      associate.WerkerFirstName,
      associate.WerkerLastName,
      associate.WerkerCompany,
      associate.WerkerProfilePicURL
    );
  };

  if (!associateId) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "var(--ion-color-medium)",
          padding: "40px",
          textAlign: "center",
        }}
      >
        <IonIcon
          icon={personOutline}
          style={{ fontSize: "64px", marginBottom: "16px" }}
        />
        <h3>Select an Associate</h3>
        <p>Choose an associate from the list to view their details</p>
      </div>
    );
  }

  if (!associate) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <IonSpinner
        />
      </div>
    );
  }

  const status = associate.AssociateStatus || associate.status;

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        padding: "32px",
        backgroundColor: "var(--ion-background-color)",
      }}
    >
      {/* Avatar and Name */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <IonAvatar
          style={{
            width: "120px",
            height: "120px",
            margin: "0 auto 16px",
          }}
        >
          <img
            src={associate.WerkerProfilePicURL || FALLBACK_AVATAR}
            alt=""
            onError={(e: any) => {
              e.target.src = FALLBACK_AVATAR;
            }}
          />
        </IonAvatar>
        <h1 style={{ margin: "0 0 8px", fontSize: "1.8rem" }}>
          {associate.WerkerFirstName} {associate.WerkerLastName}
        </h1>
        {(associate.Occupation || associate.WerkerCompany) && (
          <p style={{ color: "var(--ion-color-medium)", fontSize: "1.1rem", margin: 0 }}>
            {associate.Occupation || associate.WerkerCompany}
          </p>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ marginBottom: "32px" }}>
        {status === ASSOCIATESTATUS_TYPES.RequestReceived && (
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
            <IonButton
              expand="block"
              onClick={handleAcceptRequest}
              color="success"
              style={{ flex: 1 }}
            >
              Accept Request
            </IonButton>
            <IonButton
              expand="block"
              onClick={handleDeclineRequest}
              color="danger"
              fill="outline"
              style={{ flex: 1 }}
            >
              Decline
            </IonButton>
          </div>
        )}

        {status === ASSOCIATESTATUS_TYPES.RequestSent && (
          <IonButton
            expand="block"
            onClick={handleCancelRequest}
            color="medium"
            fill="outline"
          >
            Cancel Request
          </IonButton>
        )}

        {status === "none" && (
          <IonButton
            expand="block"
            onClick={handleSendRequest}
            color="primary"
          >
            Send Request
          </IonButton>
        )}

      </div>

      {/* Details Section */}
      <div style={{ marginTop: "24px" }}>
        {/* Company */}
        {associate.WerkerCompany && (
          <div style={{ marginBottom: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <IonIcon
                icon={businessOutline}
                style={{ fontSize: "20px", color: "var(--ion-color-medium)" }}
              />
              <h3 style={{ margin: 0, fontSize: "0.9rem", fontWeight: 600, color: "var(--ion-color-medium)" }}>
                COMPANY
              </h3>
            </div>
            <p style={{ margin: 0, paddingLeft: "28px", fontSize: "1.1rem" }}>{associate.WerkerCompany}</p>
          </div>
        )}

        {/* Bio */}
        {associate.WerkerBio && (
          <div style={{ marginBottom: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <IonIcon
                icon={personOutline}
                style={{ fontSize: "20px", color: "var(--ion-color-medium)" }}
              />
              <h3 style={{ margin: 0, fontSize: "0.9rem", fontWeight: 600, color: "var(--ion-color-medium)" }}>
                BIO
              </h3>
            </div>
            <p
              style={{
                margin: 0,
                paddingLeft: "28px",
                fontSize: "1rem",
                lineHeight: "1.5",
                whiteSpace: "pre-wrap",
              }}
            >
              {associate.WerkerBio}
            </p>
          </div>
        )}

        {/* Status Badge */}
        <div style={{ marginTop: "32px", paddingTop: "16px", borderTop: "1px solid var(--ion-color-light)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "0.9rem", color: "var(--ion-color-medium)" }}>Status:</span>
            {status === ASSOCIATESTATUS_TYPES.Associate ? (
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <IonButton
                  size="small"
                  color="success"
                  fill="outline"
                  disabled
                  style={{ margin: 0 }}
                >
                  Connected
                </IonButton>
                <IonButton
                  size="small"
                  color="medium"
                  onClick={handleDropAssociate}
                  style={{ margin: 0 }}
                >
                  Drop Associate
                </IonButton>
              </div>
            ) : (
              <span
                style={{
                  height: "28px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor:
                    status === ASSOCIATESTATUS_TYPES.RequestReceived
                      ? "var(--ion-color-warning-tint)"
                      : status === ASSOCIATESTATUS_TYPES.RequestSent
                      ? "var(--ion-color-tertiary-tint)"
                      : "var(--ion-color-light)",
                  color:
                    status === ASSOCIATESTATUS_TYPES.RequestReceived
                      ? "var(--ion-color-warning-shade)"
                      : status === ASSOCIATESTATUS_TYPES.RequestSent
                      ? "var(--ion-color-tertiary-shade)"
                      : "var(--ion-color-medium)",
                }}
              >
                {status === ASSOCIATESTATUS_TYPES.RequestReceived && "Request Received"}
                {status === ASSOCIATESTATUS_TYPES.RequestSent && "Request Sent"}
                {status === "none" && "Not Connected"}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
